<template>
  <div class="container mx-auto px-4 py-4 text-gray-900 dark:text-gray-100">
    <div class="flex flex-col gap-6">
      <PageHeading
        title="Users"
        description="Manage user accounts, invitations, and permissions"
        :crumbs="[{ label: 'User Management' }, { label: 'Users' }]"
      >
        <template #actions>
          <PagePrimaryButton @click="openInviteModal">
            <UserPlusIcon class="h-4 w-4 shrink-0" />
            Add New User
          </PagePrimaryButton>
        </template>
      </PageHeading>

      <div class="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div class="relative flex-1">
          <MagnifyingGlassIcon
            class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
          <input
            v-model="userSearch"
            type="search"
            placeholder="Search by name or email..."
            aria-label="Search users"
            class="h-9 w-full rounded-md border border-gray-300 bg-transparent py-1 pl-9 pr-3 text-sm text-gray-900 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div class="relative">
            <select
              v-model="statusFilter"
              aria-label="Status"
              :class="[userFilterClass, 'w-[140px]']"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
          <div class="relative">
            <select v-model="roleFilter" aria-label="Role" :class="[userFilterClass, 'w-[130px]']">
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="helper">Helper</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
          <div class="relative">
            <select
              v-model="loginFilter"
              aria-label="Last login"
              :class="[userFilterClass, 'w-[150px]']"
            >
              <option value="any">Any Time</option>
              <option value="never">Never</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
        </div>
      </div>

      <div
        v-if="error"
        class="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/40"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400 dark:text-red-300"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-300">Error</h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-200">{{ error }}</div>
          </div>
        </div>
      </div>

      <div
        class="rounded-xl border border-gray-200 bg-white py-6 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div class="overflow-x-auto px-6">
          <div v-if="loading" class="py-8 text-center">
            <div
              class="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"
            ></div>
          </div>

          <div v-else-if="filteredUsers.length === 0" class="py-8 text-center">
            <p class="text-gray-500 dark:text-gray-400">
              {{ users.length === 0 ? "No users found." : "No users match these filters." }}
            </p>
          </div>

          <DataTable
            v-else
            :value="filteredUsers"
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
          >
            <Column field="username" header="User" sortable>
              <template #body="{ data }">
                <div class="flex items-center gap-3">
                  <span
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                  >
                    {{ data.username.charAt(0).toUpperCase() }}
                  </span>
                  <span class="font-medium text-gray-900 dark:text-white">{{ data.username }}</span>
                </div>
              </template>
            </Column>

            <Column field="email" header="Email" sortable>
              <template #body="{ data }">
                <span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{
                  data.email
                }}</span>
              </template>
            </Column>

            <Column field="role" header="Role" sortable>
              <template #body="{ data }">
                <span
                  :class="roleBadgeClass(data.role)"
                  class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                >
                  {{ roleLabel(data.role) }}
                  <i v-if="data.role === 'owner'" class="pi pi-crown" title="Owner"></i>
                </span>
              </template>
            </Column>

            <Column field="isActive" header="Status" sortable>
              <template #body="{ data }">
                <span
                  :class="statusBadgeClass(data.isActive)"
                  class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
                >
                  {{ data.isActive ? "Active" : "Inactive" }}
                </span>
              </template>
            </Column>

            <Column field="lastLogin" header="Last Login" sortable>
              <template #body="{ data }">
                <span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatRelative(data.lastLogin) }}
                </span>
              </template>
            </Column>

            <Column field="createdAt" header="Created" sortable>
              <template #body="{ data }">
                <span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatRelative(data.createdAt) }}
                </span>
              </template>
            </Column>

            <Column header="" :exportable="false" style="width: 96px">
              <template #body="{ data }">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                    :title="data.id === authStore.user?.id ? 'Edit profile' : 'Edit user'"
                    @click="openUserProfile(data)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </button>
                  <button
                    v-if="data.id !== authStore.user?.id"
                    :disabled="deletingUserId === data.id"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950/40"
                    :title="deletingUserId === data.id ? 'Deleting...' : 'Delete user'"
                    @click="handleDeleteUser(data)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <div
        class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div class="border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Registration Settings</h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Control user registration access
          </p>
        </div>
        <div class="p-6">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h3 class="text-lg font-medium text-gray-900 dark:text-white">Registration page</h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                New users can create an account while this is enabled. While it is disabled, the
                registration page sends them back to login.
              </p>
            </div>
            <div class="flex shrink-0 items-center">
              <span class="mr-3 text-sm text-gray-500 dark:text-gray-400">
                {{ registrationEnabled ? "Enabled" : "Disabled" }}
              </span>
              <button
                type="button"
                role="switch"
                :aria-checked="registrationEnabled"
                aria-label="Registration page"
                :disabled="settingsLoading"
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  registrationEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                ]"
                @click="toggleRegistration"
              >
                <span
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    registrationEnabled ? 'translate-x-5' : 'translate-x-0'
                  ]"
                />
              </button>
            </div>
          </div>
          <div v-if="settingsError" class="mt-4 text-sm text-red-600">
            {{ settingsError }}
          </div>
        </div>
      </div>
    </div>

    <!-- Invite User Modal -->
    <div
      v-if="showInviteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="cancelInvite"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-user-title"
        aria-describedby="invite-user-description"
        class="relative w-full max-w-md overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-gray-900"
      >
        <button
          type="button"
          class="absolute right-4 top-4 rounded-sm text-gray-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400"
          aria-label="Close"
          @click="cancelInvite"
        >
          <XMarkIcon class="h-4 w-4" />
        </button>

        <div class="flex flex-col">
          <div class="border-b border-primary-500/10 bg-primary-500/5 p-6 dark:bg-primary-400/10">
            <div class="mb-2 flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-300"
              >
                <EnvelopeIcon class="h-5 w-5" />
              </div>
              <div>
                <h2
                  id="invite-user-title"
                  class="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
                >
                  Invite User
                </h2>
                <p id="invite-user-description" class="text-xs text-gray-500 dark:text-gray-400">
                  Send an invitation email to add a new team member
                </p>
              </div>
            </div>
          </div>

          <form class="space-y-6 bg-white p-6 dark:bg-gray-900" @submit.prevent="handleInviteUser">
            <div v-for="field in inviteTextFields" :key="field.id" class="space-y-2">
              <label :for="field.id" :class="inviteLabelClass">{{ field.label }}</label>
              <div class="relative">
                <component
                  :is="field.icon"
                  class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                />
                <input
                  :id="field.id"
                  v-model="inviteForm[field.key]"
                  :type="field.type"
                  required
                  :name="field.name"
                  :placeholder="field.placeholder"
                  :class="[inviteInputClass, 'pl-9']"
                />
              </div>
            </div>

            <div class="space-y-2">
              <label for="invite-role" :class="inviteLabelClass">Role</label>
              <div class="relative">
                <select
                  id="invite-role"
                  v-model="inviteForm.role"
                  :class="[inviteInputClass, 'appearance-none pr-9']"
                >
                  <option value="user">User</option>
                  <option value="helper">Helper</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <ChevronDownIcon
                  class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>

            <div
              v-if="inviteError"
              class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-700 dark:bg-red-900/40 dark:text-red-300"
            >
              {{ inviteError }}
            </div>

            <div
              class="flex items-center gap-3 rounded-lg border border-primary-500/10 bg-primary-500/5 p-3 text-xs text-gray-500 dark:text-gray-400"
            >
              <InformationCircleIcon
                class="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300"
              />
              <p>Invited users will receive an email to set up their account password.</p>
            </div>

            <div class="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <button
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                @click="cancelInvite"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="inviteLoading"
                class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary-600 px-8 text-sm font-medium text-white shadow-lg shadow-primary-500/20 transition-all hover:scale-[1.02] hover:bg-primary-700 disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100"
              >
                <EnvelopeIcon class="h-4 w-4" />
                {{ inviteLoading ? "Sending..." : "Send Invitation" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/40"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete User
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete <strong>{{ userToDelete?.username }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <Button label="Cancel" severity="secondary" class="flex-1" @click="cancelDelete" />
            <Button
              :disabled="deletingUserId !== null"
              :label="deletingUserId !== null ? 'Deleting...' : 'Delete'"
              severity="danger"
              :loading="deletingUserId !== null"
              class="flex-1"
              @click="confirmDelete"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import {
  ChevronDownIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
  XMarkIcon
} from "@heroicons/vue/24/outline";
import api from "../../services/api";
import { useAuthStore } from "../../stores/auth";
import PageHeading from "../../components/common/PageHeading.vue";
import PagePrimaryButton from "../../components/common/PagePrimaryButton.vue";
import {
  formatRelative,
  roleLabel,
  roleBadgeClass,
  statusBadgeClass
} from "../../utils/userDisplay";

export default {
  name: "AdminUsers",
  components: {
    DataTable,
    Column,
    Button,
    ChevronDownIcon,
    EnvelopeIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    UserIcon,
    UserPlusIcon,
    XMarkIcon,
    PageHeading,
    PagePrimaryButton
  },
  setup() {
    const toast = useToast();
    const router = useRouter();
    const authStore = useAuthStore();
    const users = ref([]);
    const loading = ref(true);
    const error = ref("");
    const registrationEnabled = ref(false);
    const settingsLoading = ref(false);
    const settingsError = ref("");
    const showDeleteModal = ref(false);
    const userToDelete = ref(null);
    const deletingUserId = ref(null);
    const rows = ref(10);
    const userSearch = ref("");
    const statusFilter = ref("all");
    const roleFilter = ref("all");
    const loginFilter = ref("any");
    const userFilterClass =
      "h-9 appearance-none rounded-md border border-gray-300 bg-transparent py-2 pl-3 pr-8 text-sm text-gray-900 shadow-sm outline-none focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:text-white";
    const showInviteModal = ref(false);
    const inviteForm = ref({
      username: "",
      email: "",
      role: "user"
    });
    const inviteError = ref("");
    const inviteLoading = ref(false);
    const inviteLabelClass =
      "text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400";
    const inviteInputClass =
      "h-11 w-full rounded-md border border-gray-300 bg-transparent px-3 text-sm text-gray-900 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white";
    const inviteTextFields = [
      {
        id: "invite-username",
        key: "username",
        label: "Username",
        type: "text",
        name: "username",
        placeholder: "jane",
        icon: UserIcon
      },
      {
        id: "invite-email",
        key: "email",
        label: "Email",
        type: "email",
        name: "email",
        placeholder: "name@company.com",
        icon: EnvelopeIcon
      }
    ];

    const fetchUsers = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await api.get("/api/users");
        if (response.data.success) {
          users.value = response.data.data.users;
        } else {
          error.value = response.data.message || "Failed to fetch users";
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
        error.value = err.response?.data?.message || "Failed to fetch users";
      } finally {
        loading.value = false;
      }
    };

    const filteredUsers = computed(() => {
      const query = userSearch.value.trim().toLowerCase();
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      return users.value.filter((user) => {
        const haystack =
          `${user.username || ""} ${user.firstName || ""} ${user.lastName || ""} ${user.email || ""}`.toLowerCase();
        if (query && !haystack.includes(query)) return false;
        if (statusFilter.value === "active" && !user.isActive) return false;
        if (statusFilter.value === "inactive" && user.isActive) return false;
        if (roleFilter.value !== "all" && user.role !== roleFilter.value) return false;
        if (loginFilter.value === "never") return !user.lastLogin;
        if (loginFilter.value === "7d" || loginFilter.value === "30d") {
          if (!user.lastLogin) return false;
          const maxAge = (loginFilter.value === "7d" ? 7 : 30) * day;
          return now - new Date(user.lastLogin).getTime() <= maxAge;
        }
        return true;
      });
    });

    const fetchRegistrationSettings = async () => {
      try {
        settingsLoading.value = true;
        settingsError.value = "";
        const response = await api.get("/api/settings/registration");
        if (response.data.success) {
          registrationEnabled.value = !!response.data.data.registrationEnabled;
        }
      } catch (err) {
        console.error("Failed to fetch registration settings:", err);
        settingsError.value =
          err.response?.data?.message || "Failed to fetch registration settings";
      } finally {
        settingsLoading.value = false;
      }
    };

    const toggleRegistration = async () => {
      try {
        settingsLoading.value = true;
        settingsError.value = "";
        const nextEnabled = !registrationEnabled.value;
        const response = await api.put("/api/settings/registration", {
          registrationEnabled: nextEnabled
        });
        if (response.data.success) {
          registrationEnabled.value = nextEnabled;
        } else {
          settingsError.value = response.data.message || "Failed to update registration settings";
        }
      } catch (err) {
        console.error("Failed to update registration settings:", err);
        settingsError.value =
          err.response?.data?.message || "Failed to update registration settings";
      } finally {
        settingsLoading.value = false;
      }
    };

    const handleDeleteUser = (user) => {
      // FAILSAFE: Prevent self-deletion (frontend check)
      const currentUserId = authStore.user?.id;
      if (!currentUserId) {
        error.value = "Unable to verify user identity. Please refresh the page.";
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      // Compare as both numbers and strings to handle type differences
      const userIdNum = typeof user.id === "number" ? user.id : parseInt(user.id);
      const currentUserIdNum =
        typeof currentUserId === "number" ? currentUserId : parseInt(currentUserId);

      if (userIdNum === currentUserIdNum || String(user.id) === String(currentUserId)) {
        error.value =
          "You cannot delete your own account. This action is blocked for security reasons.";
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      userToDelete.value = user;
      showDeleteModal.value = true;
    };

    const openUserProfile = (user) => {
      router.push(`/users/${user.id}`);
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      userToDelete.value = null;
    };

    const openInviteModal = () => {
      inviteForm.value = {
        username: "",
        email: "",
        role: "user"
      };
      inviteError.value = "";
      showInviteModal.value = true;
    };

    const cancelInvite = () => {
      showInviteModal.value = false;
      inviteForm.value = {
        username: "",
        email: "",
        role: "user"
      };
      inviteError.value = "";
    };

    const handleInviteUser = async () => {
      try {
        inviteLoading.value = true;
        inviteError.value = "";

        const response = await api.post("/api/users/invite", {
          username: inviteForm.value.username,
          email: inviteForm.value.email,
          role: inviteForm.value.role
        });

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "User invited successfully! Invitation email has been sent.",
            life: 3000
          });
          // Refresh users list
          await fetchUsers();
          cancelInvite();
        } else {
          inviteError.value = response.data.message || "Failed to invite user";
        }
      } catch (err) {
        console.error("Invite user error:", err);
        inviteError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to invite user";
      } finally {
        inviteLoading.value = false;
      }
    };

    const confirmDelete = async () => {
      if (!userToDelete.value) return;

      // FAILSAFE: Final check before deletion (in case state changed)
      const currentUserId = authStore.user?.id;
      const userIdToDelete = userToDelete.value.id;

      if (
        currentUserId &&
        (userIdToDelete === currentUserId || String(userIdToDelete) === String(currentUserId))
      ) {
        error.value =
          "You cannot delete your own account. This action is blocked for security reasons.";
        showDeleteModal.value = false;
        userToDelete.value = null;
        setTimeout(() => {
          error.value = "";
        }, 5000);
        return;
      }

      try {
        deletingUserId.value = userToDelete.value.id;
        error.value = "";

        const response = await api.delete(`/api/users/${userToDelete.value.id}`);

        if (response.data.success) {
          // Remove user from local list
          users.value = users.value.filter((u) => u.id !== userToDelete.value.id);
          showDeleteModal.value = false;
          userToDelete.value = null;
        } else {
          error.value = response.data.message || "Failed to delete user";
        }
      } catch (err) {
        console.error("Failed to delete user:", err);
        // Check if error is about self-deletion
        const errorMessage = err.response?.data?.message || "Failed to delete user";
        if (
          errorMessage.includes("cannot delete your own account") ||
          errorMessage.includes("your own account")
        ) {
          error.value =
            "You cannot delete your own account. This action is blocked for security reasons.";
        } else {
          error.value = errorMessage;
        }
      } finally {
        deletingUserId.value = null;
      }
    };

    onMounted(() => {
      fetchUsers();
      fetchRegistrationSettings();
    });

    return {
      authStore,
      users,
      filteredUsers,
      userSearch,
      statusFilter,
      roleFilter,
      loginFilter,
      userFilterClass,
      loading,
      error,
      fetchUsers,
      formatRelative,
      roleLabel,
      roleBadgeClass,
      statusBadgeClass,
      registrationEnabled,
      settingsLoading,
      settingsError,
      toggleRegistration,
      showDeleteModal,
      userToDelete,
      deletingUserId,
      handleDeleteUser,
      cancelDelete,
      confirmDelete,
      openUserProfile,
      openInviteModal,
      cancelInvite,
      handleInviteUser,
      showInviteModal,
      inviteForm,
      inviteError,
      inviteLoading,
      inviteLabelClass,
      inviteInputClass,
      inviteTextFields,
      rows
    };
  }
};
</script>

<style>
.dark .p-datatable table {
  background-color: #111827;
  color: #e5e7eb;
}

.dark .p-datatable .p-datatable-thead > tr > th {
  background-color: #1f2937;
  color: #e5e7eb;
  border-color: #374151;
}

.dark .p-datatable .p-datatable-tbody > tr > td {
  background-color: #111827;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-datatable .p-datatable-tbody > tr.p-highlight > td {
  background-color: #1f2937;
}

.dark .p-datatable .p-datatable-paginator-bottom {
  background-color: #0f172a;
  color: #e5e7eb;
  border-top: 1px solid #1f2937;
}

.dark .p-paginator .p-paginator-page,
.dark .p-paginator .p-paginator-prev,
.dark .p-paginator .p-paginator-next,
.dark .p-paginator .p-paginator-first,
.dark .p-paginator .p-paginator-last {
  background-color: #0f172a;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-paginator .p-paginator-page.p-highlight {
  background-color: #1f2937;
  color: #d1d5db;
  border-color: #334155;
}
</style>
